import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { useLazyQuery, useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, Card, PageHeader, Button } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import DatePicker from "components/cck/DatePicker";
import Helmet from "components/helmet/Helmet";
import NumberInput from "components/cck/NumberInput";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";

import { DateUtils } from "utils/DateUtils";

import { gasoilPriceSchema, GasoilPriceSchemaInput, GasoilPriceSchemaOutput } from "./utils/gasoilPrice.schema";

import { GasoilPriceSource } from "__generated__/enums";
import { GetGasoilPriceDocument, GetGasoilPriceQuery } from "__generated__/view/gasoilprices/graphql/queries.generated";
import { InsertOrUpdateGasoilPriceDocument, InsertOrUpdateGasoilPriceMutation } from "__generated__/view/gasoilprices/graphql/mutations.generated";

const GasoilPriceCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const { control, handleSubmit, reset } = useForm<GasoilPriceSchemaInput, unknown, GasoilPriceSchemaOutput>({
		resolver: zodResolver(gasoilPriceSchema),
		defaultValues: {
			id: null,
			date: null,
			price: null,
			priceDifference: null,
			percentageDifference: null,
			source: GasoilPriceSource.User,
			pricedQuantity: null,
		},
	});

	const [ getGasoilPrice ] = useLazyQuery<GetGasoilPriceQuery>(GetGasoilPriceDocument, {
		onCompleted: response => {
			reset({
				id: response.gasoilPrice?.id,
				date: response.gasoilPrice?.date ?? null,
				price: response.gasoilPrice?.price ?? null,
				priceDifference: response.gasoilPrice?.priceDifference ?? null,
				percentageDifference: response.gasoilPrice?.percentageDifference ?? null,
				source: response.gasoilPrice?.source ?? GasoilPriceSource.User,
				pricedQuantity: response.gasoilPrice?.pricedQuantity ?? null,
			});

			setEditable(response.gasoilPrice?.source === GasoilPriceSource.User);
		},
	});

	const [ createOrEditGasoilPrice, { loading } ] = useMutation<InsertOrUpdateGasoilPriceMutation>(InsertOrUpdateGasoilPriceDocument);

	const save = (data: GasoilPriceSchemaOutput) => {
		createOrEditGasoilPrice({
			variables: {
				input: data,
			},
		}).then(() => {
			navigate("/view/gasoilprices");
		});
	};

	const [ editable, setEditable ] = useState(true);

	useEffect(() => {
		if (id) {
			getGasoilPrice({
				variables: {
					id: Number(id),
				},
			});
		}
	}, []);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("GASOIL_PRICE")}
					<Helmet title={intl.formatMessage({ id: "GASOIL_PRICE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/gasoilprices")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} disabled={!editable || loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card>
						<FormGroup
							label={Language("GASOIL_PRICES_STARTDATE")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="date"
								control={control}
								disabled={!editable}
								render={({ field, formState }) => (
									<React.Fragment>
										<DatePicker
											{...field}
											value={DateUtils.getTimestamp(field.value) ?? undefined}
											onChange={date => field.onChange(date ? moment(date).format("YYYY-MM-DD") : null)} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={intl.formatMessage({ id: "GASOIL_PRICES_PRICE" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name='price'
								control={control}
								disabled={!editable}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput
											{...field}
											addonBefore={intl.formatMessage({ id: "LANGUAGE_CURRENCY" })} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label={intl.formatMessage({ id: "GASOIL_PRICES_PRICEDIFFERENCE" })} labelPlacement="left-center" overflow="hidden">
							<Controller
								name='priceDifference'
								control={control}
								disabled={!editable}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={intl.formatMessage({ id: "LANGUAGE_CURRENCY" })} />
								)} />
						</FormGroup>

						<FormGroup label={intl.formatMessage({ id: "GASOIL_PRICES_PERCENTAGEDIFFERENCE" })} labelPlacement="left-center" overflow="hidden">
							<Controller
								name='percentageDifference'
								control={control}
								disabled={!editable}
								render={({ field }) => <NumberInput {...field} addonAfter="%" />} />
						</FormGroup>

						<FormGroup label={intl.formatMessage({ id: "GASOIL_PRICES_SOURCE" })} labelPlacement="left-center" overflow="hidden">
							<Controller
								name='source'
								control={control}
								render={({ field }) => <Input {...field} disabled />} />
						</FormGroup>

						<FormGroup label={intl.formatMessage({ id: "GASOIL_PRICES_PRICED_QUANTITY" })} labelPlacement="left-center" overflow="hidden">
							<Controller
								name='pricedQuantity'
								control={control}
								disabled={!editable}
								render={({ field }) => <NumberInput {...field} />} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default GasoilPriceCreateEdit;
